import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-none text-xs font-bold uppercase tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-tb-green text-tb-porcelain hover:bg-[var(--tb-green-deep)]',
        secondary:
          'bg-tb-oxide text-tb-porcelain hover:bg-[var(--tb-oxide-deep)]',
        outline:
          'border border-[var(--fg)] text-[var(--fg)] hover:bg-[var(--fg)] hover:text-tb-porcelain',
        ghost: 'text-tb-oxide hover:text-tb-green',
        link: 'h-auto border-b border-[var(--fg)] px-0 pb-[1px] font-display font-normal text-base normal-case tracking-normal text-[var(--fg)] hover:border-tb-green hover:text-tb-green',
        destructive: 'bg-[var(--tb-error)] text-tb-white hover:opacity-90',
        text: 'h-auto w-fit gap-2 bg-transparent p-0 font-normal normal-case tracking-normal transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)]',
        icon: 'size-11 shrink-0 gap-0 bg-transparent p-0 font-normal normal-case tracking-normal',
      },
      size: { sm: 'h-9 px-4', md: 'h-11 px-6', lg: 'h-12 px-8' },
    },
    // `text` and `icon` define their OWN sizing (`h-auto w-fit p-0` / `size-11
    // p-0`) rather than participating in `sm`/`md`/`lg`. The `size` variant
    // still resolves to its `md` default even when the call site omits it
    // (cva always applies `defaultVariants`), which would otherwise leak
    // `h-11 px-6` through — `tailwind-merge` (via `cn()`) resolves conflicting
    // utilities by "last one wins," and `size`'s classes are computed AFTER
    // `variant`'s in cva's output. These compound variants re-assert the
    // sizing classes AFTER `size` in that same output, so they win over the
    // `size` default while still losing (correctly) to anything the call site
    // passes via `className` (merged in last).
    compoundVariants: [
      { variant: ['text'], class: 'h-auto w-fit gap-2 p-0' },
      { variant: ['icon'], class: 'size-11 gap-0 p-0' },
    ],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
